"use client";

import { useEffect, useState } from "react";
import { useRouter, useParams } from "next/navigation";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSession } from "next-auth/react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { TextField, SelectField, SwitchField } from "@admin/_components/form-fields";
import { useApiPost } from "@admin/_lib/api-hooks";

// ── Schema ────────────────────────────────────────────────────────────────────

const answerSchema = z.object({
  answer: z.string().min(1, "Answer text is required"),
  correct_answer: z.boolean(),
});

const quizSchema = z.object({
  question: z.string().min(1, "Question is required"),
  lesson_id: z.string().min(1, "Lesson is required"),
  priority: z.number().min(1, "Priority is required"),
  review_start_time_in_second: z.number().optional(),
  review_end_time_in_second: z.number().optional(),
  // Stored as boolean internally; converted to 0/1 at submit to match backend expectations
  status: z.boolean(),
  answers: z
    .array(answerSchema)
    .min(1, "At least one answer is required")
    .refine((arr) => arr.some((a) => a.correct_answer), {
      message: "At least one answer must be marked as correct",
    }),
});

type QuizValues = z.infer<typeof quizSchema>;

// ── Helpers ───────────────────────────────────────────────────────────────────

function getBaseUrl() {
  return process.env.NEXT_PUBLIC_API_CLOUDFRONT || "https://api.divineinfinitebeing.com/";
}

// ── Component ─────────────────────────────────────────────────────────────────

export function QuizFormPage({ mode }: { mode: "add" | "edit" }) {
  const router = useRouter();
  const params = useParams();
  const quizId = params?.id as string | undefined;
  const { data: session } = useSession();
  const isEdit = mode === "edit" && !!quizId;

  const [fetchError, setFetchError] = useState<string | null>(null);

  // Fetch lesson options
  const { data: lessonsData } = useQuery({
    queryKey: ["lessons-list"],
    queryFn: async () => {
      const res = await fetch(`${getBaseUrl()}admin/lesson-list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        // Explicit pagination body to reliably retrieve all lessons (matches Angular behaviour)
        body: JSON.stringify({ condition: {}, limit: 200, skip: 0 }),
      });
      const json = await res.json();
      return (json.results?.res ?? json.results ?? json.dt?.res ?? []) as { _id: string; lesson_name: string }[];
    },
    enabled: !!session?.token,
    staleTime: 5 * 60 * 1000,
  });

  const lessonOptions = (lessonsData ?? []).map((l) => ({
    value: l._id,
    label: l.lesson_name,
  }));

  // Fetch existing quiz for edit
  const { data: quizData, isLoading: isFetchingQuiz } = useQuery({
    queryKey: ["quiz-edit", quizId],
    queryFn: async () => {
      const res = await fetch(`${getBaseUrl()}admin/quiz-edit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ _id: quizId }),
      });
      if (!res.ok) throw new Error("Failed to fetch quiz");
      const json = await res.json();
      return json.results?.res ?? json.result ?? json.results ?? json;
    },
    enabled: isEdit && !!session?.token,
    retry: false,
    throwOnError: false,
  });

  const addMutation = useApiPost("admin/quiz-add", {
    invalidateKeys: ["admin/quiz-list", "admin/quiz-list-count"],
  });
  const editMutation = useApiPost("admin/quiz-update", {
    invalidateKeys: ["admin/quiz-list", "admin/quiz-list-count"],
  });

  const form = useForm<QuizValues>({
    resolver: zodResolver(quizSchema),
    defaultValues: {
      question: "",
      lesson_id: "",
      priority: 1,
      review_start_time_in_second: undefined,
      review_end_time_in_second: undefined,
      status: true,
      answers: [{ answer: "", correct_answer: false }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "answers",
  });

  const { reset } = form;

  useEffect(() => {
    if (quizData) {
      const answers = Array.isArray(quizData.answers) && quizData.answers.length > 0
        ? quizData.answers.map((a: { answer?: string; correct_answer?: boolean }) => ({
            answer: a.answer ?? "",
            correct_answer: a.correct_answer ?? false,
          }))
        : [{ answer: "", correct_answer: false }];

      reset({
        question: quizData.question ?? "",
        lesson_id: quizData.lesson_id ?? "",
        priority: quizData.priority ?? 1,
        review_start_time_in_second: quizData.review_start_time_in_second,
        review_end_time_in_second: quizData.review_end_time_in_second,
        // Backend stores 0/1; convert to boolean for the Switch
        status: quizData.status !== 0,
        answers,
      });
    }
  }, [quizData, reset]);

  async function onSubmit(values: QuizValues) {
    try {
      // Normalize status boolean → 0/1 and priority to integer for backend compatibility
      const normalized = {
        ...values,
        status: values.status ? 1 : 0,
        priority: Math.round(values.priority),
      };
      const payload = isEdit ? { ...normalized, _id: quizId } : normalized;
      if (isEdit) {
        await editMutation.mutateAsync(payload);
        toast.success("Quiz updated successfully");
      } else {
        await addMutation.mutateAsync(payload);
        toast.success("Quiz created successfully");
      }
      router.push("/admin/training/quizzes");
    } catch (err: unknown) {
      setFetchError(null);
      toast.error(err instanceof Error ? err.message : "Failed to save quiz");
    }
  }

  if (isEdit && isFetchingQuiz) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 text-sm text-muted-foreground">
        <p>{fetchError}</p>
        <Button variant="outline" onClick={() => router.back()}>Go Back</Button>
      </div>
    );
  }

  const isPending = addMutation.isPending || editMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-7 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? "Edit Quiz" : "Add Quiz"}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isEdit ? "Update quiz question and answers" : "Create a new quiz with answers"}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={() => router.back()}>
          <ArrowLeft className="size-4" />
        </Button>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Core fields */}
          <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
            <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
              <CardTitle className="text-sm font-medium">Quiz Details</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <TextField control={form.control} name="question" label="Question" placeholder="Enter the quiz question" />

              <div className="grid gap-4 md:grid-cols-2">
                <SelectField
                  control={form.control}
                  name="lesson_id"
                  label="Lesson"
                  options={lessonOptions}
                  placeholder="Select a lesson"
                />
                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          placeholder="1"
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="review_start_time_in_second"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Review Start (seconds)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          placeholder="Optional"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="review_end_time_in_second"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Review End (seconds)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          placeholder="Optional"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <SwitchField control={form.control} name="status" label="Active" />
            </CardContent>
          </Card>

          {/* Answers array */}
          <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
            <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-[#25aceb]" />
                  Answers
                  <span className="text-xs font-normal text-muted-foreground">(mark at least one correct)</span>
                </CardTitle>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs gap-1"
                  onClick={() => append({ answer: "", correct_answer: false })}
                >
                  <Plus className="size-3" /> Add Answer
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              {fields.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No answers yet. Click &quot;Add Answer&quot; to begin.
                </p>
              )}

              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-start gap-3 p-3 rounded-lg"
                  style={{ background: "hsl(240 20% 13%)", border: "1px solid hsl(240 20% 20%)" }}
                >
                  {/* Correct answer checkbox */}
                  <FormField
                    control={form.control}
                    name={`answers.${index}.correct_answer`}
                    render={({ field: cf }) => (
                      <FormItem className="flex items-center pt-2.5">
                        <FormControl>
                          <Checkbox
                            checked={cf.value}
                            onCheckedChange={cf.onChange}
                            className="border-[#25aceb] data-[state=checked]:bg-[#25aceb] data-[state=checked]:border-[#25aceb]"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Answer text */}
                  <div className="flex-1">
                    <FormField
                      control={form.control}
                      name={`answers.${index}.answer`}
                      render={({ field: af }) => (
                        <FormItem>
                          <FormLabel className="text-xs text-muted-foreground">
                            Answer {index + 1}
                            {form.watch(`answers.${index}.correct_answer`) && (
                              <span className="ml-2 text-[#25aceb]">✓ Correct</span>
                            )}
                          </FormLabel>
                          <FormControl>
                            <Input placeholder={`Enter answer ${index + 1}`} {...af} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Remove */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 shrink-0 mt-5 text-destructive hover:text-destructive"
                    disabled={fields.length === 1}
                    onClick={() => remove(index)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              ))}

              {/* Show array-level error (e.g., "at least one correct") */}
              {form.formState.errors.answers?.root?.message && (
                <p className="text-sm text-destructive">{form.formState.errors.answers.root.message}</p>
              )}
              {typeof form.formState.errors.answers?.message === "string" && (
                <p className="text-sm text-destructive">{form.formState.errors.answers.message}</p>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button type="submit" disabled={isPending}>
              {isPending ? (isEdit ? "Updating..." : "Creating...") : (isEdit ? "Update Quiz" : "Create Quiz")}
            </Button>
            <Button type="button" variant="outline" onClick={() => router.back()}>
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
